import { useTranslation } from "react-i18next";
import { CheckCircle2, ShieldAlert, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollFadeIn, fadeCls, fadeStyle } from "@/hooks/useScrollFadeIn";

/**
 * "Hvem er det til" + "Vi siger fra" — nordisk troværdighedssektion.
 * Viser typiske situationer Relark hjælper med, og — vigtigere — situationer
 * hvor Relark eksplicit IKKE er det rette værktøj. Det aktive fravalg er et af
 * de stærkeste troværdighedssignaler et nordisk B2B-produkt kan sende.
 */
export default function RelarkAudienceFit() {
  const { t } = useTranslation("ui");
  const { ref, isVisible } = useScrollFadeIn(0.1);

  const situationsRaw = t("relark.audience.situations", { returnObjects: true });
  const allSituations: string[] = Array.isArray(situationsRaw)
    ? (situationsRaw as string[])
    : [];
  // Vis kun de 5 primære på forsiden — resten linker vi til
  const situations = allSituations.slice(0, 5);
  const hasMore = allSituations.length > 5;

  const notForRaw = t("relark.audience.notFor", { returnObjects: true });
  const notFor: string[] = Array.isArray(notForRaw) ? (notForRaw as string[]) : [];

  return (
    <section id="audience" className="py-16 sm:py-24">
      <div ref={ref} className="mx-auto max-w-5xl px-5">
        <div className={`text-center max-w-2xl mx-auto ${fadeCls(isVisible)}`}>
          <h2 className="text-2xl font-bold sm:text-3xl">
            {t("relark.audience.title")}
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            {t("relark.audience.sub")}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Situations Relark hjælper med */}
          <div
            className={`rounded-2xl border-2 border-primary/20 bg-primary/[0.03] p-6 sm:p-7 ${fadeCls(isVisible)}`}
            style={fadeStyle(150)}
          >
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-primary">
                {t("relark.audience.situationsTitle")}
              </h3>
            </div>
            <ul className="space-y-3">
              {situations.map((item, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-foreground/85 leading-relaxed">
                  <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-primary/70" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {hasMore && (
              <Link
                to="/features#situations"
                className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline underline-offset-4"
              >
                {t("relark.audience.seeAllCta", {
                  defaultValue: "Se alle situationer",
                })}
                <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </div>

          {/* Vi siger fra */}
          <div
            className={`rounded-2xl border-2 border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20 p-6 sm:p-7 ${fadeCls(isVisible)}`}
            style={fadeStyle(300)}
          >
            <div className="flex items-center gap-2 mb-4">
              <ShieldAlert className="h-5 w-5 text-amber-700 dark:text-amber-400" />
              <h3 className="font-semibold text-amber-800 dark:text-amber-300">
                {t("relark.audience.notForTitle")}
              </h3>
            </div>
            <ul className="space-y-3">
              {notFor.map((item, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-foreground/85 leading-relaxed">
                  <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-amber-700/70 dark:text-amber-400/70" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}